/**
 * Per-user search history (previous searches / replay).
 *
 * Backed by the server (`/api/search-history`, scoped to the logged-in user)
 * so history follows the user across devices/browsers — unlike the old
 * localStorage-only version. Records the FULL replayable request payload so a
 * click re-runs the exact search (mode + filters + keyword groups/rows).
 */
import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi } from '../services/authApi';
import type { SearchDescriptor, SearchHistoryEntry } from '../types';

export const SEARCH_HISTORY_LIMIT = 30;
const QUERY_KEY = ['search-history'];

/**
 * Legacy shape from the old localStorage hook. Only still referenced by the
 * retired SearchSidebar component (dead code, kept for type-compat). New code
 * uses SearchHistoryEntry from ../types.
 * @deprecated
 */
export interface SearchHistoryItem {
  query: string;
  timestamp: number;
  id: string;
}

export function useSearchHistory(limit = SEARCH_HISTORY_LIMIT) {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<SearchHistoryEntry[]>({
    queryKey: QUERY_KEY,
    queryFn: async () => (await authApi.listSearchHistory(limit)).entries,
    staleTime: 30 * 1000,
    // Anonymous / not-logged-in → backend returns []; a transient error
    // shouldn't blow up the search page, so don't retry.
    retry: false,
  });

  const recordMut = useMutation({
    mutationFn: (d: SearchDescriptor) =>
      authApi.recordSearchHistory({
        mode: d.mode,
        label: d.label,
        payload: d.payload,
        ...(d.result_count != null ? { result_count: d.result_count } : {}),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const removeMut = useMutation({
    mutationFn: (id: number) => authApi.deleteSearchHistory(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const clearMut = useMutation({
    mutationFn: () => authApi.clearSearchHistory(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  // Fire-and-forget; never throw into the search flow if persistence fails.
  const recordSearch = useCallback(
    (d: SearchDescriptor) => {
      if (!d.label?.trim()) return;
      recordMut.mutate(d);
    },
    [recordMut],
  );

  const removeSearch = useCallback((id: number) => removeMut.mutate(id), [removeMut]);
  const clearHistory = useCallback(() => clearMut.mutate(), [clearMut]);

  return {
    history: data ?? [],
    isLoading,
    recordSearch,
    removeSearch,
    clearHistory,
  };
}
